import {Box, Typography} from '@mui/material';

import {ReactComponent as ExportIcon} from '../../../assests/svg/export_icon.svg';
import {t} from 'i18next';
import {useEffect, useRef, useState} from 'react';
import {
  getHighLevelAlerts,
  updateAlertStatusAsViewed,
} from '../../../api/UserManagementService';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import AurAlertCarousel from '../../common/aur-alert/AurAlertCarousel';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {getExportReportXlsx} from '../../../api/ReportingManagement';
import {
  downloadResponseAsXLSX,
  getFilenameFromHeaders,
  getUTCDateTimeForDisplay,
} from '../../../utils/utils';
import HighLevelProductionMetricTable, {
  HighLevelProductionMetricTableHandles,
} from './HighLevelProductionTable';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_REPORT_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';
import AurButton from '../../common/aur-button/AurButton';

interface UserAlertData {
  userAlertId: number;
  triggeredOn: string;
  alertTitle: string;
  alertDescription: string;
  alertType: string;
}

const HighLevelProductionReportDetails = () => {
  const {trackGAEvent} = useAnalytics();
  const tableRef = useRef<HighLevelProductionMetricTableHandles>(null);
  const [alertsHighLevel, setAlertsHighLevel] = useState<UserAlertData[]>([]);
  const [toggleAlertState, setToggleAlertState] = useState(false);
  const [isEventTracked, setIsEventTracked] = useState(false);
  const [isFailedToLoadAlert, setIsFailedToLoadAlert] =
    useState<boolean>(false);
  const {projectId, workspaceId, clientId} = useSelector(
    (state: RootState) => state.reports
  );

  const refreshDates = useSelector(
    (state: RootState) => state.reports.refreshDates
  );

  const [isAlertLoading, setIsAlertLoading] = useState(false);
  const apiResponseTime = useRef('');

  const fetchAlertData = async () => {
    setIsAlertLoading(true);
    setIsFailedToLoadAlert(false);
    if (clientId && projectId && workspaceId) {
      try {
        const responseFetchAlert = await getHighLevelAlerts(
          clientId,
          projectId,
          workspaceId
        );
        setAlertsHighLevel(responseFetchAlert?.data?.data);
        apiResponseTime.current = responseFetchAlert.data.responseTime;
      } catch (error) {
        setIsFailedToLoadAlert(true);
        apiResponseTime.current = (error as any).responseTime;
      } finally {
        setIsAlertLoading(false);
        !isEventTracked &&
          trackGAEvent({
            eventName: GA_EVENT_NAMES.PAGE_VIEW,
            page: GA_DATA_MODE_AUTO,
            user: GA_DATA_MODE_AUTO,
            report: {
              name: t('gaTracking.reports.tabProductionMetrics'),
              type: GA_REPORT_TYPE.STANDARD,
              load_time: apiResponseTime.current,
            },
          });
        setIsEventTracked(true);
      }
    }
  };
  useEffect(() => {
    if (projectId && workspaceId && clientId) {
      fetchAlertData();
    }
  }, [clientId, projectId, workspaceId, toggleAlertState]);

  const markAlertAsViewed = async (userAlertId: number) => {
    await updateAlertStatusAsViewed(userAlertId);
    setToggleAlertState((prevState) => !prevState);
  };

  const handleDownloadXlsx = () => {
    handleDownloadReportXlsx();
  };

  const handleDownloadReportXlsx = async () => {
    try {
      const response = await getExportReportXlsx(
        clientId as string,
        projectId as string,
        workspaceId as string,
        '/high-level-production-metrics'
      );
      const fileName =
        getFilenameFromHeaders(response.headers['content-disposition']) ??
        `${workspaceId}_${Date.now()}`;
      downloadResponseAsXLSX(response, fileName, false);
    } catch (error: any) {
      console.error('Error downloading the CSV:', error?.message);
    }
  };

  return (
    <>
      <Box className="processing-summary-and-exceptions-details">
        <div className="export-button ">
          <div className="high-level-production-report-section">
            <Typography
              className="high-level-production-report-title typo-text subhead1_size"
              component="h1"
            >
              {t('reports.highLevelProductionMetrics.label')}
            </Typography>

            <AurButton
              onClick={() => {
                handleDownloadXlsx();
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.EXPORT,
                  click: {
                    name: t('gaTracking.reports.exportOption.xlsx'),
                    type: GA_CLICK_TYPES.BUTTON,
                    region: GA_REGIONS.BODY,
                    action: t('gaTracking.reports.exportReport'),
                  },
                  file_extension: GA_FILE_EXTENTIONS.XLSX,
                });
              }}
              startIcon={<ExportIcon aria-hidden />}
              type="continue"
              size="Medium"
              label={t('common.exportXLSX')}
              dataTestId="export-XLSX-btn"
              classNames="typo-text buttonMedium_size"
            />
          </div>
          <div className="high-level-production-report-date-section">
            <Typography>
              <span className="typo-text body2_size color-data">
                {t('reports.highLevelProductionMetrics.description1')}
              </span>
              <AurLightTooltip title={t('common.yearMonthDate')}>
                <span className="color-mist-100">
                  {getUTCDateTimeForDisplay(
                    refreshDates.highlevelProductionMetrics || ''
                  )}
                </span>
              </AurLightTooltip>
              <br />
              <span className="typo-text body2_size color-data">
                {t('reports.highLevelProductionMetrics.description3')}&nbsp;
                {t('reports.highLevelProductionMetrics.content')}
              </span>
            </Typography>
          </div>
        </div>

        {alertsHighLevel.length > 0 ? (
          <Box className="high-level-alert">
            <span id="hosting-user-summary" className="addUserHint">
              {t('reports.hostingUserSummary.alertHints')}
            </span>
            <div
              data-testid="alert"
              aria-describedby={'hosting-user-summary'}
              aria-roledescription="carousel"
              aria-live="polite"
            >
              <AurAlertCarousel
                alertsData={
                  alertsHighLevel?.map((alert) => ({
                    ...alert,
                    deleteButtonHandler: () =>
                      markAlertAsViewed(alert.userAlertId),
                  })) as []
                }
              />
            </div>
          </Box>
        ) : isAlertLoading ? (
          <div className="alert-loading">
            <AurLoadingIndicator />
          </div>
        ) : null}
        {isFailedToLoadAlert ? (
          <div className="alert-error">
            <AurDataGridErrorOverlay onRefresh={() => fetchAlertData()} />
          </div>
        ) : null}
      </Box>
      <div className="high-level-production-report-content">
        <HighLevelProductionMetricTable ref={tableRef} />
      </div>
    </>
  );
};

export default HighLevelProductionReportDetails;
